"use client";

import { useTranslations } from "next-intl";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { IconCheck, IconInfo } from "@/components/icons";
import { colors } from "@/theme/colors";
import { cardTitleSx, priceSx, pricingCardSx } from "@/theme/tokens";
import { selectableHandlers } from "@/lib/interaction";
import { Checklist } from "./Checklist";
import {
  type CarePeriod,
  usePricingSelection,
} from "./PricingSelectionContext";

/**
 * Per-period copy. Annual is the recommended value, so its note reads as a
 * saving (rendered in green) and it carries the effective yearly figure.
 */
const PERIODS: Record<
  CarePeriod,
  {
    labelKey: string;
    subKey: string;
    priceKey: string;
    yearlyKey: string | null;
    noteKey: string;
    best: boolean;
  }
> = {
  monthly: {
    labelKey: "care.billMonthly",
    subKey: "care.monthlySub",
    priceKey: "care.price",
    yearlyKey: null,
    noteKey: "care.monthlyNote",
    best: false,
  },
  annual: {
    labelKey: "care.billAnnual",
    subKey: "care.annualSub",
    priceKey: "care.priceAnnual",
    yearlyKey: "care.annualYearly",
    noteKey: "care.annualNote",
    best: true,
  },
};

const PERIOD_ORDER: readonly CarePeriod[] = ["monthly", "annual"];

/**
 * Care & support — the recurring add-on, shown as the fourth pricing card
 * alongside the package cards. Its title, price, and CTA sit at the same heights
 * as the package cards (shared title height + `mt: auto` button); a
 * Monthly/Annual toggle below the price switches the billing period, and the Add
 * button opts the add-on into the selection. Selection lives in the shared
 * PricingSelectionContext. Client component.
 */
export function CareAddOn() {
  const t = useTranslations("pricing");
  const { carePeriod, setCarePeriod, careIncluded, toggleCare } =
    usePricingSelection();
  const features = [0, 1, 2].map((i) => t(`care.features.${i}`));
  const period = PERIODS[carePeriod];

  return (
    <Paper
      elevation={0}
      sx={pricingCardSx(careIncluded)}
    >
      <Typography variant="h3" sx={cardTitleSx}>
        {t("care.name")}
      </Typography>

      {/* Price — same position and style as the package prices. */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography sx={priceSx}>{t(period.priceKey)}</Typography>
        <Typography color="text.secondary" sx={{ fontSize: "0.95rem" }}>
          {t("care.unit")}
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ fontSize: "0.8rem", mb: 2 }}>
        {t(period.subKey)}
        {period.yearlyKey ? ` ${t(period.yearlyKey)}` : ""}
      </Typography>

      {/* Billing-period toggle — sits under the price it controls. */}
      <Box
        role="radiogroup"
        aria-label={t("care.name")}
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          mb: 2.5,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {PERIOD_ORDER.map((key) => {
          const active = key === carePeriod;
          return (
            <Box
              key={key}
              role="radio"
              tabIndex={0}
              aria-checked={active}
              aria-label={t(PERIODS[key].labelKey)}
              {...selectableHandlers(() => setCarePeriod(key))}
              sx={{
                cursor: "pointer",
                py: 0.85,
                fontSize: "0.85rem",
                fontWeight: 500,
                textAlign: "center",
                color: active ? "background.default" : "text.secondary",
                backgroundColor: active ? "text.primary" : "transparent",
                transition: "background-color 0.15s ease, color 0.15s ease",
                outlineOffset: 2,
              }}
            >
              {t(PERIODS[key].labelKey)}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontWeight: 600, mb: 2.5 }}>
        {t("care.tagline")}
      </Typography>

      <Checklist items={features} />

      {/* Pinned to the bottom so the button lines up with the package CTAs;
          the fine print sits just above it. */}
      <Box sx={{ mt: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            mb: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: period.best ? 600 : 400,
              color: period.best ? colors.success : "text.secondary",
            }}
          >
            {t(period.noteKey)}
          </Typography>
          <IconInfo size={14} style={{ flexShrink: 0 }} />
        </Box>
        <Button
          fullWidth
          variant={careIncluded ? "contained" : "outlined"}
          aria-pressed={careIncluded}
          onClick={toggleCare}
          startIcon={
            careIncluded ? (
              <IconCheck size={16} />
            ) : undefined
          }
          sx={{ py: 1.1 }}
        >
          {t(careIncluded ? "careAdded" : "care.add")}
        </Button>
      </Box>
    </Paper>
  );
}
